import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { PiezeoDashboardService } from "../../../../../app/services/piezo-dashboard.service";
import { PiezoAnomalyCard } from "../piezo-anomaly-status/piezo-anomaly-status.component";
import { BaseUrlConstantsService } from "../../../../../app/services/base-url-constants.service";
import { BackendApiService } from "../../../../../app/services/backend-api.service";
import { PowertrainModalComponent } from "../../../powertrain-modal/powertrain-modal.component";
import { PowerTrainService } from "../../../../../app/services/powertrain.service";
interface SensorPosition {
  x: number;
  y: number;
  is_new: boolean;
  subType: string;
  assetType: string;
  associated: boolean;
  configured: boolean;
  gateway_id: number;
  no_of_axis: number;
  assetMarker: string;
  xPercentage: number;
  yPercentage: number;
  sensorDNames: string;
  sensorMarker: string | number;
  sensors_info: Record<string, string | number>;
  serial_number: number;
  form_configured: boolean;
  temperature_channel: number;
  is_temperature_added: number;
  asset_status?: number;
  mac_address?: string;
  motor_serial?: string;
  color?: string;
  asset_id: number;
  inventory_id: number;
  master_type: string | number;
  sensor_number: number;
  connected_sensors: SensorPosition[];
  device_status: number;
}

interface PiezoData {
  id: string;
  subsystem_uuid: string;
  subsystem_name: string;
  client_id: string;
  Image_path: string;
  device_position: string;
  overall_health: string;
  overall_live_status: string;
}

@Component({
  selector: "app-piezo-image-container",
  templateUrl: "./piezo-image.component.html",
  styleUrls: ["./piezo-image.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    PiezoAnomalyCard,
    PowertrainModalComponent,
  ],
})
export class PiezoImageContainer implements OnInit, OnDestroy {
  @Input("overall_status") overall_status: number | string;
  @Input("piezoData") piezoData: PiezoData | null = null;

  piezoImg: string = "";
  piezoName: string = "";
  sensors: SensorPosition[] = [];
  client_id: string = "";
  piezoPhyImgs: any[] = [];
  subsystem_id: any = "";

  show_powertrain_img = false;
  showImgModal = false;
  addPhyImgModal = false;
  selected_img: any = {};
  parentAssetId: number;
  latestFFTId: number;
  latestKurtosisId: number;
  bearingIsOn: boolean = false;

  oldParamsSubscription = new Subscription();
  oldParams: any = null;

  piezo_dash_srv = inject(PiezeoDashboardService);
  pt_service = inject(PowerTrainService);

  add_physical_img_modal = false;
  show_physical_image = false;

  constructor(private router: Router, private service: BackendApiService) {}

  ngOnInit(): void {
    sessionStorage.removeItem("latestFFTId");
    sessionStorage.removeItem("FFTId");
    sessionStorage.removeItem("latestKurtosisId");
    sessionStorage.removeItem("KurtosisId");
    this.piezo_dash_srv.subsystem_data$.subscribe((data) => {
      console.log("Subsystem data updated in piezo image:", data);
      this.piezoData = data;
      this.overall_status=this.piezoData?.overall_live_status;
      this.subsystem_id = data.id;
      this.initializePiezoData();
    });
    this.fetchPhysicalImages();
  }

  ngOnDestroy(): void {
    this.oldParamsSubscription.unsubscribe();
  }

  initializePiezoData(): void {
    if (!this.piezoData) return;

    this.client_id = this.piezoData.client_id;
    this.piezoImg = this.piezoData.Image_path;
    this.piezoName = this.piezoData.subsystem_name;

    if (typeof this.piezoData.device_position === "string") {
      this.sensors = JSON.parse(this.piezoData.device_position);
    } else {
      this.sensors = this.piezoData.device_position as any;
    }

    const masterAsset = this.sensors.find(
      (sensor) => sensor.assetMarker.toLowerCase() === "master"
    );
    this.parentAssetId = masterAsset ? masterAsset.asset_id : null;

    this.sensors = this.sensors.map((sensor) => ({
      ...sensor,
      color: sensor.color,
    }));
  }

  async fetchLatestFFTAndKurtosis(sensor: SensorPosition) {
    try {
      const apiUrl = `${BaseUrlConstantsService.PIEZO_LATEST_FFT_AND_KURTOSIS_IDS}${sensor.asset_id}/${sensor.inventory_id}/${this.client_id}/${sensor.mac_address}/${sensor.assetType}`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.status === "success") {
        const body = response.body || response.data?.body;
        this.latestFFTId = body.fftId;
        this.latestKurtosisId = body.kurtosisId;
        sessionStorage.setItem("latestFFTId", String(this.latestFFTId));
        sessionStorage.setItem("FFTId", String(this.latestFFTId));
        sessionStorage.setItem(
          "latestKurtosisId",
          String(this.latestKurtosisId)
        );
        sessionStorage.setItem("KurtosisId", String(this.latestKurtosisId));
      }
    } catch (error) {
      console.error("Error loading crest factor", error);
    } finally {
    }
  }

  async fetchBearing(sensor: SensorPosition) {
    try {
      const apiUrl = `${BaseUrlConstantsService.PIEZO_BEARING_ANALYSIS_API}${sensor.asset_id}`;
      const response: any = await this.service.getDataV2(apiUrl);
      if (response?.data?.status === "success") {
        this.bearingIsOn = Boolean(
          response.data.body?.bearing_analysis_key !== "NA"
        );
      }
    } catch (error) {
      console.error("Error loading bearing analysis", error);
    } finally {
    }
  }

  async onSensorClick(sensor: SensorPosition): Promise<void> {
    console.log("Sensor clicked:", sensor);
    console.log(sensor.master_type);

    if (sensor.master_type != 4) {
      let childs = this.sensors.filter(
        (s) => s.asset_id !== sensor.asset_id
      ) as SensorPosition[];

      let master: any = this.sensors.find((s) => s.assetMarker === "MASTER");
      let masterCopy = { ...master };
      delete masterCopy.connected_sensors; // safety
      let others = this.sensors.filter(
        (s) => s.master_type != 4 && s.asset_id !== master.asset_id
      );
      master.connected_sensors = [masterCopy, ...others];
      master.connected_sensors[0].id = master.connected_sensors[0].asset_id;
      master.id = master.connected_sensors[0].asset_id;
      console.log(master);
      let obj = {
        deviceId: sensor.inventory_id,
        assetid: sensor.asset_id,
        label: sensor.sensor_number,
        asset_type: sensor.assetType,
        sub_type: sensor.subType,
        device_serial_no: sensor.serial_number,
        serial_no: sensor.sensor_number,
        assetStatus: sensor.device_status || 2,
        overallStatus: this.piezoData.overall_live_status || 2,
        sensorType: "",
        no_of_children: childs.length,
        macaddress: sensor.mac_address,
        route_path: "device-reports",
        outOfService: "false",
        motor_serial_no: sensor.serial_number,
        master_type: sensor.master_type,
        asset_master_info: JSON.stringify(master),
      };
      console.log(master);
      console.log(obj)
      this.router.navigate(["/detail-nxt"], {
        skipLocationChange: true,
        queryParams: { ...obj },
      });
    } else {
      await this.fetchLatestFFTAndKurtosis(sensor);
      await this.fetchBearing(sensor);
      const navigationExtras = {
        skipLocationChange: true,
        queryParams: {
          ...sensor,
          parentAssetId: this.parentAssetId,
          latestFFTId: this.latestFFTId,
          latestKurtosisId: this.latestKurtosisId,
          sensors_info: JSON.stringify(sensor.sensors_info),
          bearingIsOn: this.bearingIsOn,
        },
      };
      console.log(
        "------------------------------------Navigation -----------------------------"
      );
      console.log(navigationExtras);
      console.log(this.piezoData);
      this.router.navigate(["/piezeo-sensor-dashboard"], navigationExtras);
    }
  }

  editPiezo(): void {
    this.router.navigate(["/piezeo-edit", this.piezoData]);
  }

  viewPiezoImages(): void {
    this.show_powertrain_img = true;
  }

  addImage(): void {
    this.add_physical_img_modal = true;
  }

  showPhysicalImage(image: any) {
    this.selected_img = image;
    this.show_physical_image = true;
  }

  closeModal(event?: any): void {
    if (event && event.type == "savePhyImg") {
      this.fetchPhysicalImages();
    }
    this.add_physical_img_modal = false;
    this.show_powertrain_img = false;
    this.show_physical_image = false;
  }

  showTooltip(sensor: any): string {
    const {
      subType,
      sensorDNames,
      assetType,
      gateway_id,
      master_type,
      mac_address: mac,
      serial_number: serial,
    } = sensor;
    let sub_type = assetType == "MOTOR" ? `Sub-Type: ${subType}` : "";
    let type = `Type: ${assetType}`;
    let display_name = `Sensor: ${sensorDNames}`;
    let marker = `Marker: ${sensor.assetMarker}`;
    let master__type =
      master_type == 4
        ? `Master-Type: Piezo`
        : master_type == 3
        ? `Master-Type: EmStd + RTDs`
        : master_type == 2
        ? `Master-Type: Em-Standard`
        : "Master-Type: Em-Mini";
    let mac_address = `MAC: ${mac}`;
    let serial_number = `Serial: ${serial}`;
    return `
      ${display_name}
      ${type}
      ${sub_type}
      ${marker}
      ${master__type}
      ${mac_address}
      ${serial_number}

    `;
  }

  get isAssetRunning(): boolean {
    return this.overall_status == 1;
  }

  fetchPhysicalImages() {
    this.pt_service
      .fetchPhysicalImages(
        `${BaseUrlConstantsService.POWERTRAIN_PHYSICAL_IMG}${this.subsystem_id}`
      )
      .then((res: any) => {
        this.piezoPhyImgs = res.data;
      })
      .catch((err: any) => {
        console.log(err);
      });
  }
}
